import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { SessionUser } from '@mys/contracts';
import { api, ApiError, forgetCsrfToken, setUnauthorizedListener } from './api.js';

/* ============================================================================
   Who is logged in.
   ----------------------------------------------------------------------------
   The session lives in an httpOnly cookie, which JavaScript cannot read by
   design. So the app cannot know whether it is logged in without asking:
   `GET /api/auth/me` on load is not an optimisation to remove later, it is the
   only way to answer the question after a refresh.

   `status` is four-valued rather than a boolean, and each value is a different
   ANSWER to "are we logged in?":

     loading       we have not asked yet
     authenticated yes
     anonymous     no — the API said 401
     unavailable   we could not ask

   Collapsing the first into the third is what makes a dashboard flash its
   login screen on every reload. Collapsing the LAST into it is worse and
   shipped here: the bootstrap caught every error and called it `anonymous`, so
   a 429, a 502 or one dropped request on a weak connection threw away a
   perfectly good session cookie and dumped the owner at a login form — where
   the right password would also have failed. Found by walking the screens in
   a real browser: ten of them redirected to /login while holding a valid
   session, purely because the rate limiter answered 429. See CONTEXT.md §15.
   ========================================================================== */

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous' | 'unavailable';

interface SessionValue {
  status: SessionStatus;
  user: SessionUser | null;
  /**
   * Sign in with a password.
   *
   * Returns what happened rather than resolving void, because a correct
   * password is no longer the same thing as a session. When the account has
   * two-factor on, the API sets no cookie and hands back a five-minute token
   * instead; the caller has to collect a code and call `completeTwoFactor`.
   *
   * A `void` return here was the shape until 2026-10-12 and it would have been
   * a real bug: the old body did `setUser(response.user)` unconditionally, and
   * on the two-factor path `user` is absent — so the app would have gone
   * `authenticated` with a null user and no session cookie at all.
   */
  login: (email: string, password: string) => Promise<LoginOutcome>;
  /** The second step. Resolves to a session, or throws like any other call. */
  completeTwoFactor: (token: string, proof: SecondFactor) => Promise<void>;
  logout: () => Promise<void>;
  /** Called when any request 401s, so one dead session logs the whole app out. */
  expire: () => void;
  /**
   * True when the session ENDED rather than never existing.
   *
   * Since the idle window shipped, being sent to the login screen is something
   * that happens to people who were working half an hour ago, and a login form
   * that appears with no explanation reads as a bug. This is the difference
   * between "please log in" and "you were signed out because nothing happened
   * for a while" — and only the second one is true here.
   */
  expired: boolean;
  /** Re-read who is signed in, in place. See below. */
  refresh: () => Promise<void>;
  /** Ask again after an `unavailable`. */
  retry: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

interface MeResponse {
  ok: true;
  user: SessionUser;
}

/** What a password got us. Discriminated, so neither branch can be read as
    the other by a caller that forgets to check. */
export type LoginOutcome = { mfaRequired: false } | { mfaRequired: true; mfaToken: string };

/** One of the two, never both — the API refuses a request carrying both. */
export type SecondFactor = { code: string } | { recoveryCode: string };

interface LoginResponse {
  ok: true;
  mfaRequired?: boolean;
  mfaToken?: string;
  user?: SessionUser;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);

  /** Bumped by `retry`, which re-runs the effect below. */
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => {
    setStatus('loading');
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    api
      .get<MeResponse>('/api/auth/me', controller.signal)
      .then((response) => {
        setUser(response.user);
        setStatus('authenticated');
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setUser(null);
        /* Only a 401 means "not logged in". Anything else — 429, 5xx, the
           request never landing — means we could not ask, and answering "you
           are logged out" to that question is a lie that costs the user their
           session. `ApiError.isUnauthorized` exists for exactly this
           distinction; the original code did not consult it. */
        setStatus(error instanceof ApiError && error.isUnauthorized ? 'anonymous' : 'unavailable');
      });

    return () => controller.abort();
  }, [attempt]);

  /**
   * Re-read the session WITHOUT flashing the loading state.
   *
   * `retry` is for a bootstrap that failed and has to show it is trying again;
   * this is for a screen that just changed the signed-in user's own name and
   * needs the shell and the greeting to say so. A failure is deliberately
   * swallowed: the edit itself already succeeded, and downgrading a good
   * session because a follow-up read blipped is the exact bug `unavailable`
   * was added to prevent.
   */
  const refresh = useCallback(async () => {
    try {
      const response = await api.get<MeResponse>('/api/auth/me');
      setUser(response.user);
      setStatus('authenticated');
    } catch {
      // Intentionally ignored — see above.
    }
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginOutcome> => {
    /* Cleared on the attempt, not on success: the explanation has been read by
       the time somebody is typing, and leaving it up next to a "wrong
       password" error says two contradictory things at once. */
    setExpired(false);
    const response = await api.post<LoginResponse>('/api/auth/login', { email, password });

    /* Checked on `mfaRequired` AND on the absence of a user, not on one of
       them. Either alone would make a response shape nobody expected into a
       silent "logged in" with nothing behind it. */
    if (response.mfaRequired === true || !response.user) {
      if (!response.mfaToken) throw new Error('The server did not say how to finish logging in.');
      return { mfaRequired: true, mfaToken: response.mfaToken };
    }

    setUser(response.user);
    setStatus('authenticated');
    return { mfaRequired: false };
  }, []);

  const completeTwoFactor = useCallback(async (token: string, proof: SecondFactor) => {
    const response = await api.post<MeResponse>('/api/auth/2fa', { token, ...proof });
    setUser(response.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      /* Clearing an httpOnly cookie needs the server, so a failure here means
         the cookie may survive. Still drop the local state: staying "logged
         in" on screen after the user asked to leave is the worse outcome, and
         the next request will 401 anyway. */
      if (!(error instanceof ApiError)) throw error;
    }
    forgetCsrfToken();
    setUser(null);
    setStatus('anonymous');
    /* Pressing "log out" is not being signed out. Saying so would be an
       explanation of something the person just did on purpose. */
    setExpired(false);
  }, []);

  const [expired, setExpired] = useState(false);

  const expire = useCallback(() => {
    forgetCsrfToken();
    setUser(null);
    setStatus('anonymous');
    setExpired(true);
  }, []);

  /* One dead session logs the whole app out, wherever the 401 surfaced.
     Registered here rather than checked at each call site, because the call
     site that gets forgotten is the one that strands the user on a screen full
     of errors they cannot act on. `expire` is stable, so this runs once. */
  useEffect(() => setUnauthorizedListener(expire), [expire]);

  const value = useMemo<SessionValue>(
    () => ({ status, user, expired, login, completeTwoFactor, logout, expire, retry, refresh }),
    [status, user, expired, login, completeTwoFactor, logout, expire, retry, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}
